import { useEffect, useMemo, useState } from 'react';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import useTopology from '@api/hooks/topology/useTopology';
import { getIsPrimeEnabled } from '@api/selectors/topology';
import {
  hasActiveSubscriptionToGoal,
  hasActiveIconicSubscriptionToGoal,
  hasActiveLiteSubscriptionToGoal
} from '@models/Me';
import useSubscriptionExpired from '@hooks/useSubscriptionExpired';
import usePurchaseCTAHandler from '@hooks/usePurchaseCTAHandler';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import pushRoute from '@utils/pushRoute';
import useEnrollInfo from '@cont/SeeAll/TestSeriesListing/TestSeriesRevamp/useEnrollInfo';
import { useSnackbar } from '@stores/snackbar';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import useIsRecordedGoal from '@hooks/useIsRecordedGoal';
import useFollowGoal from '@api/hooks/useFollowGoal';
import { getCourseEnrollMessage, subscriptionTypes } from '@constants/index';
import usePrimeQuotaStore from '@stores/prime-quota/store';
import { PrimeMode, UserPlatformPlan } from '@stores/prime-quota/types';
import usePrimeTrialPromoModal from '@stores/prime-pitch/usePrimeTrialPromoModal';
import { PrimeTrialPromoContextualId } from '@stores/prime-pitch/types';
import { segment } from '@actions/analytics';
import { USER_FREE_QUOTA_REACHED } from '@constants/segment';
import { getPrimeGlobalProps } from '@stores/prime-quota/selectors';
import { readCookie } from '@utils/cookies';
import {
  isFreeTest,
  isIconicTest,
  normalizeSubscriptionFlags
} from '@cont/SeeAll/TestSeriesListing/testTab/subscription';
import getIsFreeUserForEnrollMessage from '@utils/getIsFreeUserForEnrollMessage';
import usePlusIconicSubscribed from '@hooks/usePlusIconicSubscribed';

const DEFAULT_PRIME_CTA_LABEL = 'Join Prime now at ₹1';

const useSyllabusProgrammePanel = ({
  programmeUID,
  goalUID,
  goalSlug,
  programme = {}
}) => {
  const { is_part_of_batch: isPartOfBatch } = programme;
  const subscriptionFlags = normalizeSubscriptionFlags(programme);
  const { isIconic, isSpecial } = subscriptionFlags;
  const iconicTest = isIconicTest(subscriptionFlags);
  const freeTest = isFreeTest(subscriptionFlags);

  const { topology: goalTopology } = useTopology(goalUID);
  const isPrimeGoal = getIsPrimeEnabled(goalTopology);

  const { myInfo } = useMyInfo();
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { openElement: showLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const { open: showSnackbar } = useSnackbar();
  const { followCurrentGoal } = useFollowGoal(goalUID);
  const selectedGoalUID = useSelectedGoalUID();
  const isRecordedGoal = useIsRecordedGoal(goalUID);
  const { openPrimeTrialPromoModal } = usePrimeTrialPromoModal();

  const accessToken = readCookie('accessToken');
  const fetchQuotaDetails = usePrimeQuotaStore(
    (state) => state.fetchQuotaDetails
  );
  const isFetchingQuotaDetails = usePrimeQuotaStore(
    (state) => state.isFetchingQuotaDetails
  );
  const userPlatformPlan = usePrimeQuotaStore(
    (state) => state.userPlatformPlan
  );
  const getQuotaForMode = usePrimeQuotaStore((state) => state.getQuotaForMode);

  const { enrolledState, onToggleEnroll, isEnrollStateValidating } =
    useEnrollInfo(programmeUID, goalUID, { revalidateOnMount: true });

  const isEnrolled = enrolledState?.isEnrolled;
  const isIconicUser = hasActiveIconicSubscriptionToGoal(myInfo ?? {}, goalUID);
  const isPlusUser = hasActiveSubscriptionToGoal(myInfo ?? {}, goalUID);
  const isLiteUser = hasActiveLiteSubscriptionToGoal(myInfo ?? {}, goalUID);
  const isPlusIconicSubscribed = usePlusIconicSubscribed({ goalUID });
  const isSubscribed = isIconic ? isIconicUser : isPlusUser;

  const plusExpired = useSubscriptionExpired(goalUID);
  const iconicExpired = useSubscriptionExpired(
    goalUID,
    subscriptionTypes.ICONIC
  );
  const CTALabel = usePurchaseCTAHandler(goalUID);

  const [unEnrollModalOpen, setUnEnrollModalOpen] = useState(false);

  useEffect(() => {
    if (!isPrimeGoal || !goalUID) {
      return;
    }

    fetchQuotaDetails({ goalUID, accessToken });
  }, [accessToken, fetchQuotaDetails, goalUID, isPrimeGoal]);

  const hasPrimePaidPlan = userPlatformPlan === UserPlatformPlan.PAID;
  const hasPrimeAccess = isPrimeGoal && hasPrimePaidPlan;
  const testQuota = getQuotaForMode(PrimeMode.TEST);
  const isTestQuotaExhausted = testQuota.isQuotaExhausted;

  const showPrimeTrialCta = useMemo(() => {
    if (!isPrimeGoal || !freeTest) {
      return false;
    }

    if (isFetchingQuotaDetails && userPlatformPlan === null) {
      return false;
    }

    // Matches Android: Join Prime only when daily test quota is exhausted
    return isTestQuotaExhausted;
  }, [
    freeTest,
    isFetchingQuotaDetails,
    isPrimeGoal,
    isTestQuotaExhausted,
    userPlatformPlan
  ]);

  const isCtaLoading =
    isPrimeGoal && isFetchingQuotaDetails && userPlatformPlan === null;

  const canEnrollInProgramme = (() => {
    if (iconicTest) {
      return isSubscribed;
    }

    if (freeTest) {
      if (isPrimeGoal && isTestQuotaExhausted) {
        return false;
      }

      return isSpecial || hasPrimeAccess;
    }

    return isSubscribed;
  })();

  const getButtonLabel = () => {
    if (showPrimeTrialCta) {
      return DEFAULT_PRIME_CTA_LABEL;
    }

    if (isSpecial) {
      return isEnrolled ? 'Unenroll' : 'Enroll';
    }

    if (hasPrimeAccess && isEnrolled) {
      return 'Unenroll';
    }

    if (hasPrimeAccess) {
      return 'Enroll';
    }

    if (isSubscribed && isEnrolled) {
      return 'Unenroll';
    }

    if (isSubscribed) {
      return 'Enroll';
    }

    if (iconicExpired && isIconic) {
      return 'Renew subscription';
    }

    if (
      (plusExpired && isIconic && !isIconicUser) ||
      (isIconic && isPlusUser && !isIconicUser)
    ) {
      return 'Get Iconic plan';
    }

    if (plusExpired) {
      return 'Renew subscription';
    }

    return isIconic ? 'Get Iconic plan' : CTALabel;
  };

  const getButtonVariant = () => {
    if (showPrimeTrialCta) {
      return 'prime';
    }

    if (isSpecial) {
      return isEnrolled ? 'secondary' : 'primary';
    }

    if (hasPrimeAccess && isEnrolled) {
      return 'secondary';
    }

    if (hasPrimeAccess) {
      return 'primary';
    }

    if ((iconicExpired && isIconic) || plusExpired || !isSubscribed) {
      return 'brand';
    }

    if (isSubscribed && isEnrolled) {
      return 'secondary';
    }

    return 'primary';
  };

  const navigateToSubscribe = () => {
    pushRoute(
      '/goal/[goalSlug]/[goalUID]/subscribe',
      `/goal/${goalSlug}/${goalUID}/subscribe`,
      {},
      'course'
    );
  };

  const toggleEnroll = async () => {
    if (!isLoggedIn) {
      showLoginDrawer();
      return;
    }

    if (isEnrolled) {
      setUnEnrollModalOpen(true);
      return;
    }

    const enrollResponse = await onToggleEnroll();

    if (enrollResponse?.error) {
      showSnackbar({
        message: enrollResponse.error.message || 'Something went wrong',
        type: 'error'
      });
      return;
    }

    followCurrentGoal();
    showSnackbar({
      message: getCourseEnrollMessage({
        type: 'Test-series',
        goalUID,
        selectedGoalUID,
        goalName: programme?.goal?.name,
        isRecordedGoal,
        isFreeUser: getIsFreeUserForEnrollMessage(
          myInfo,
          goalUID,
          isPlusIconicSubscribed
        )
      }),
      type: 'success'
    });
  };

  const handleCtaClick = () => {
    if (showPrimeTrialCta) {
      if (!isLoggedIn) {
        showLoginDrawer();
        return;
      }

      segment.track(USER_FREE_QUOTA_REACHED, {
        duration_limit: 'Daily',
        limit_type: 'Test',
        goal_uid: goalUID || 'NA',
        goal_name: programme?.goal?.name || 'NA',
        ...getPrimeGlobalProps()
      });
      openPrimeTrialPromoModal(
        PrimeTrialPromoContextualId.TEST,
        goalUID,
        goalSlug || goalTopology?.slug,
        {
          analyticsContext: { testUid: programme?.uid },
          dismissNavigateTo: `/goal/${goalSlug}/${goalUID}/test-series`
        }
      );
      return;
    }

    if (isLiteUser && isPartOfBatch) {
      navigateToSubscribe();
      return;
    }

    if (canEnrollInProgramme) {
      toggleEnroll();
      return;
    }

    navigateToSubscribe();
  };

  const acceptUnenrollClick = async () => {
    await onToggleEnroll();
    setUnEnrollModalOpen(false);
  };

  return {
    buttonLabel: getButtonLabel(),
    buttonVariant: getButtonVariant(),
    showPrimeTrialCta,
    isCtaLoading,
    handleCtaClick,
    isEnrollStateValidating,
    unEnrollModalOpen,
    onCloseUnenrollModal: () => setUnEnrollModalOpen(false),
    acceptUnenrollClick
  };
};

export default useSyllabusProgrammePanel;
